import React, { useState, useEffect } from "react";
import { ApiService, VideoData, FormulaData, SocialLinksData } from "../../services/apiService";
import SocialMediaIcons from "../../components/SocialMediaIcons";

const ManageContent: React.FC = () => {
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [formulas, setFormulas] = useState<FormulaData[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialLinksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<VideoData | FormulaData | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingItem, setDeletingItem] = useState<VideoData | FormulaData | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'videos' | 'formulas' | 'social-links'>('videos');
  const [imageInputType, setImageInputType] = useState<'url' | 'upload'>('url');
  const [contentImageInputType, setContentImageInputType] = useState<'url' | 'upload'>('upload');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load videos
      const videosData = await ApiService.getAllVideos();
      setVideos(videosData);
      
      // Load formulas
      const formulasData = await ApiService.getAllFormulas();
      setFormulas(formulasData);
      
      // Load social links
      const socialLinksData = await ApiService.getSocialLinks();
      setSocialLinks(socialLinksData);
      
      // Load images (removed - not needed)
      // const imagesData = await ApiService.getImages(1, 100);
      // setImages(imagesData.images);
      
    } catch (error) {
      setVideos([]); // Set empty array on error
      setFormulas([]); // Set empty array on error
      setSocialLinks(null); // Set null on error
      // setImages([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: VideoData | FormulaData) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditingItem(null);
    setShowModal(false);
  };

  // Social Links handlers
  const handleSaveSocialLinks = async (facebook: string, telegram: string) => {
    try {
      if (socialLinks && socialLinks._id) {
        // Update existing
        await ApiService.updateSocialLinks({ facebook, telegram });
      } else {
        // Create new
        await ApiService.createSocialLinks({ facebook, telegram });
      }
      
      // Reload data
      await loadData();
      alert('Cập nhật social links thành công!');
    } catch (error) {
      alert('Có lỗi xảy ra khi cập nhật social links');
    }
  };

  const handleDelete = (item: VideoData | FormulaData) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeletingItem(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingItem) return;

    try {
      const isVideo = 'videoUrl' in deletingItem;
      const itemId = deletingItem.id || deletingItem._id;
      
      if (!itemId) {
        throw new Error('ID không tồn tại');
      }

      if (isVideo) {
        await ApiService.deleteVideo(itemId);
      } else {
        await ApiService.deleteFormula(itemId);
      }
      
      setShowDeleteModal(false);
      setDeletingItem(null);
      
      // Reload data
      loadData();
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const itemType = 'videoUrl' in deletingItem ? 'video' : 'công thức';
      alert(`Có lỗi xảy ra khi xóa ${itemType}! Chi tiết: ` + errorMessage);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validation
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 5MB');
        e.target.value = '';
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Chỉ chấp nhận file ảnh: JPG, PNG, WEBP');
        e.target.value = '';
        return;
      }
      
      // Chỉ tạo preview URL, không upload ngay
      const imageUrl = URL.createObjectURL(file);
      if (editingItem && 'videoUrl' in editingItem) {
        setEditingItem({...editingItem, imageUrl, imageFile: file});
      }
    }
  };

  // Content image upload handler
  const handleContentImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validation
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 10MB');
        e.target.value = '';
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Chỉ chấp nhận file ảnh: JPG, PNG, WEBP');
        e.target.value = '';
        return;
      }
      
      // Chỉ tạo preview URL, không upload ngay
      const contentImageUrl = URL.createObjectURL(file);
      if (editingItem && !('videoUrl' in editingItem)) {
        setEditingItem({...editingItem, contentImageUrl, contentImageFile: file});
      }
    }
  };

  const handleSave = async (updatedItem: VideoData | FormulaData) => {
    setLoading(true);
    try {
      const isVideo = 'videoUrl' in updatedItem;
      const isCreate = updatedItem.id === 0 || updatedItem._id === undefined;
      
      // Validation cho Video
      if (isVideo) {
        if (!updatedItem.title?.trim()) {
          alert('Vui lòng nhập tiêu đề video');
          return;
        }
        if (!updatedItem.description?.trim()) {
          alert('Vui lòng nhập mô tả video');
          return;
        }
        if (!updatedItem.videoUrl?.trim()) {
          alert('Vui lòng nhập URL video');
          return;
        }
      }
      
      // Validation cho Formula
      if (!isVideo) {
        if (!updatedItem.order || updatedItem.order < 1) {
          alert('Vui lòng nhập thứ tự hợp lệ (>= 1)');
          return;
        }
        if (updatedItem.coinsRequired === undefined || updatedItem.coinsRequired < 0) {
          alert('Vui lòng nhập số xu cần thiết hợp lệ (>= 0)');
          return;
        }
      }

      // Upload ảnh trước khi lưu
      let finalImageUrl = updatedItem.imageUrl || '';
      let finalContentImageUrl = 'contentImageUrl' in updatedItem ? updatedItem.contentImageUrl || '' : '';

      if (isVideo) {
        // Upload ảnh video nếu có
        if (updatedItem.imageFile) {
          try {
            const videoId = (updatedItem._id || updatedItem.id)?.toString();
            if (videoId && videoId !== '0' && videoId !== 'undefined') {
              // Video đã tồn tại - dùng endpoint riêng
              const res = await ApiService.uploadVideoImage(videoId, updatedItem.imageFile);
              finalImageUrl = res.imageUrl;
            } else {
              // Video mới - dùng upload chung
              const { url } = await ApiService.uploadImage(updatedItem.imageFile);
              finalImageUrl = url;
            }
          } catch (err) {
            console.error('❌ Upload video image error:', err);
            alert('Upload ảnh video thất bại');
            return;
          }
        }

        // Handle Video operations
        if (isCreate) {
          const videoItem = updatedItem as VideoData;
          await ApiService.createVideo({
            title: videoItem.title,
            description: videoItem.description,
            videoUrl: videoItem.videoUrl,
            imageUrl: finalImageUrl
          });
        } else {
          const videoItem = updatedItem as VideoData;
          await ApiService.updateVideo(updatedItem.id || updatedItem._id || 0, {
            title: videoItem.title,
            description: videoItem.description,
            videoUrl: videoItem.videoUrl,
            imageUrl: finalImageUrl
          });
        }
      } else {
        // Upload ảnh content nếu có
        if (updatedItem.contentImageFile) {
          try {
            const formulaId = (updatedItem._id || updatedItem.id)?.toString();
            if (formulaId && formulaId !== '0' && formulaId !== 'undefined') {
              // Formula đã tồn tại - dùng endpoint riêng
              const res = await ApiService.uploadContentImage(formulaId, updatedItem.contentImageFile);
              finalContentImageUrl = res.contentImageUrl;
            } else {
              // Formula mới - dùng upload chung
              const { url } = await ApiService.uploadImage(updatedItem.contentImageFile);
              finalContentImageUrl = url;
            }
          } catch (err) {
            console.error('❌ Upload content image error:', err);
            alert('Upload ảnh content thất bại');
            return;
          }
        }

        // Handle Formula operations
        if (isCreate) {
          const formulaItem = updatedItem as FormulaData;
          await ApiService.createFormula({
            title: formulaItem.title,
            description: formulaItem.description,
            type: formulaItem.type,
            order: formulaItem.order,
            coinsRequired: formulaItem.coinsRequired,
            content: formulaItem.content,
            videoUrl: formulaItem.videoUrl,
            imageUrl: formulaItem.imageUrl || '',
            contentImageUrl: finalContentImageUrl
          });
        } else {
          const formulaItem = updatedItem as FormulaData;
          await ApiService.updateFormula(updatedItem.id || updatedItem._id || 0, {
            title: formulaItem.title,
            description: formulaItem.description,
            type: formulaItem.type,
            order: formulaItem.order,
            coinsRequired: formulaItem.coinsRequired,
            content: formulaItem.content,
            videoUrl: formulaItem.videoUrl,
            imageUrl: formulaItem.imageUrl || '',
            contentImageUrl: finalContentImageUrl
          });
        }
      }
      
      setShowModal(false);
      setEditingItem(null);
      
      await loadData(); // Reload data from API
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const itemType = 'videoUrl' in updatedItem ? 'video' : 'công thức';
      alert(`❌ Có lỗi xảy ra khi lưu ${itemType}! Chi tiết: ` + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Quản lý nội dung</h1>
          <div className="mt-2 text-sm text-gray-500">
            <p>Status: {loading ? 'Đang tải...' : `Đã tải ${videos.length} video, ${formulas.length} công thức`}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('videos')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'videos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Video ({videos.length})
              </button>
              <button
                onClick={() => setActiveTab('formulas')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'formulas'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Công thức ({formulas.length})
              </button>
              <button
                onClick={() => setActiveTab('social-links')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'social-links'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Social Links
              </button>
            </nav>
          </div>
        </div>

        {/* Content Management */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="p-6">
            <div className="space-y-4">
              {activeTab === 'videos' ? (
                <>
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Danh sách video ({videos.length})</h2>
                <button
                  onClick={() => {
                    setEditingItem({
                      id: 0,
                      title: '',
                      description: '',
                      videoUrl: '',
                          imageUrl: '',
                          imageFile: undefined,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    });
                    setShowModal(true);
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Thêm video
                </button>
              </div>

              {videos.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 text-lg mb-4">Chưa có video nào</div>
                  <div className="text-gray-400 text-sm mb-4">
                    Hãy thêm video đầu tiên bằng cách nhấn nút "Thêm video" ở trên
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((video) => (
                    <div key={video.id || video._id} className="bg-gray-50 rounded-lg p-4 border">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-800">{video.title}</h3>
                        <span className="text-sm text-gray-500">#{video.id || video._id}</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{video.description}</p>
                      
                      {/* Hiển thị thumbnail nếu có */}
                      {video.imageUrl && (
                        <div className="mb-2">
                          <img 
                            src={video.imageUrl} 
                            alt={video.title}
                            className="w-full h-32 object-cover rounded"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                          {video.viewCount ? `${video.viewCount} lượt xem` : 'Chưa có lượt xem'}
                        </span>
                        <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(video)}
                        className="text-blue-500 hover:text-blue-700 text-sm font-medium"
                      >
                        Chỉnh sửa
                      </button>
                          <button
                            onClick={() => handleDelete(video)}
                            className="text-red-500 hover:text-red-700 text-sm font-medium"
                          >
                            Xóa
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </>
              ) : activeTab === 'formulas' ? (
                <>
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold">Danh sách công thức ({formulas.length})</h2>
                    <button
                      onClick={() => {
                        setEditingItem({
                          id: 0,
                          title: '',
                          description: '',
                          type: 'article' as const,
                          order: 1,
                          coinsRequired: 0,
                          content: '',
                          imageUrl: '',
                          createdAt: new Date().toISOString(),
                          updatedAt: new Date().toISOString()
                        });
                        setShowModal(true);
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Thêm công thức
                    </button>
                  </div>

                  {formulas.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-500 text-lg mb-4">Chưa có công thức nào</div>
                      <div className="text-gray-400 text-sm mb-4">
                        Hãy thêm công thức đầu tiên bằng cách nhấn nút "Thêm công thức" ở trên
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {formulas.map((formula) => (
                        <div key={formula.id || formula._id} className="bg-gray-50 rounded-lg p-4 border">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-gray-800">{formula.title}</h3>
                            <span className="text-sm text-gray-500">#{formula.id || formula._id}</span>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">{formula.description}</p>
                          
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              formula.type === 'video' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {formula.type === 'video' ? 'Video' : 'Bài viết'}
                            </span>
                            <span className="text-xs text-gray-500">Thứ tự: {formula.order}</span>
                          </div>
                          
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-yellow-600">💰 {formula.coinsRequired} xu</span>
                          </div>
                          
                          {/* Hiển thị thumbnail nếu có */}
                          {formula.imageUrl && (
                            <div className="mb-2">
                              <img 
                                src={formula.imageUrl} 
                                alt={formula.title}
                                className="w-full h-32 object-cover rounded"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          {/* Hiển thị content image nếu có */}
                          {formula.contentImageUrl && (
                            <div className="mb-2">
                              <div className="text-xs text-gray-500 mb-1">Ảnh content:</div>
                              <img 
                                src={formula.contentImageUrl} 
                                alt={`${formula.title} content`}
                                className="w-full h-32 object-cover rounded border-2 border-blue-200"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">
                              {formula.content ? `${formula.content.length} ký tự` : 'Chưa có nội dung'}
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(formula)}
                                className="text-blue-500 hover:text-blue-700 text-sm font-medium"
                              >
                                Chỉnh sửa
                              </button>
                              <button
                                onClick={() => handleDelete(formula)}
                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                              >
                                Xóa
                              </button>
                            </div>
                    </div>
                  </div>
                ))}
              </div>
                  )}
                </>
              ) : activeTab === 'social-links' ? (
                <SocialLinksManagement 
                  socialLinks={socialLinks}
                  onSave={handleSaveSocialLinks}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showModal && editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {editingItem.id === 0 ? 'Thêm mới' : 'Chỉnh sửa'} {('videoUrl' in editingItem) ? 'video' : 'công thức'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                handleSave(editingItem);
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tiêu đề *
                    </label>
                    <input
                      type="text"
                      value={editingItem.title}
                      onChange={(e) => setEditingItem({...editingItem, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mô tả *
                    </label>
                    <textarea
                      value={editingItem.description}
                      onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      required
                    />
                  </div>

                  {('videoUrl' in editingItem) ? (
                    // Video fields
                    <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL video *
                    </label>
                    <input
                      type="url"
                      value={editingItem.videoUrl}
                      onChange={(e) => setEditingItem({...editingItem, videoUrl: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ảnh thumbnail
                          </label>
                          
                          {/* Tabs cho URL và Upload */}
                          <div className="mb-2">
                            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                              <button
                                type="button"
                                onClick={() => setImageInputType('url')}
                                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                                  imageInputType === 'url' 
                                    ? 'bg-white text-blue-600 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                URL
                              </button>
                              <button
                                type="button"
                                onClick={() => setImageInputType('upload')}
                                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                                  imageInputType === 'upload' 
                                    ? 'bg-white text-blue-600 shadow-sm' 
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                Upload
                              </button>
                            </div>
                          </div>
                          
                          {/* URL Input */}
                          {imageInputType === 'url' && (
                            <div>
                              <input
                                type="url"
                                value={editingItem.imageUrl || ''}
                                onChange={(e) => setEditingItem({...editingItem, imageUrl: e.target.value, imageFile: undefined})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="https://example.com/thumbnail.jpg"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Nhập URL ảnh từ internet
                              </p>
                            </div>
                          )}
                          
                          {/* Upload Input */}
                          {imageInputType === 'upload' && (
                            <div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Chọn ảnh từ máy tính (JPG, PNG, WEBP, tối đa 5MB)
                              </p>
                            </div>
                          )}
                          
                          {/* Preview */}
                          {(editingItem.imageUrl || editingItem.imageFile) && (
                            <div className="mt-3">
                              <div className="text-xs text-gray-500 mb-2">
                                Preview ảnh video: {editingItem.imageFile ? 'File đã chọn (chưa upload)' : editingItem.imageUrl}
                              </div>
                              <img
                                src={editingItem.imageUrl}
                                alt="Preview"
                                className="w-full h-32 object-cover rounded border"
                                onError={(e) => {
                                  console.error('❌ Video image load error:', editingItem.imageUrl);
                                  e.currentTarget.style.display = 'none';
                                }}
                                onLoad={() => {
                                  console.log('✅ Video image loaded successfully:', editingItem.imageUrl);
                                }}
                              />
                            </div>
                          )}
                        </div>
                    </>
                  ) : (
                    // Formula fields
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Loại *
                        </label>
                        <select
                          value={editingItem.type}
                          onChange={(e) => setEditingItem({...editingItem, type: e.target.value as 'article' | 'video'})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="article">Bài viết</option>
                          <option value="video">Video</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Thứ tự *
                          </label>
                          <input
                            type="text"
                            value={editingItem.order || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, ''); // Chỉ cho phép số
                              setEditingItem({...editingItem, order: value ? parseInt(value) : undefined});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nhập số thứ tự"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Số xu cần thiết *
                          </label>
                          <input
                            type="text"
                            value={editingItem.coinsRequired || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9]/g, ''); // Chỉ cho phép số
                              setEditingItem({...editingItem, coinsRequired: value ? parseInt(value) : undefined});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nhập số xu"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nội dung
                        </label>
                        <textarea
                          value={editingItem.content || ''}
                          onChange={(e) => setEditingItem({...editingItem, content: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={4}
                          placeholder="Nhập nội dung công thức..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          URL video (nếu có)
                        </label>
                        <input
                          type="url"
                          value={editingItem.videoUrl || ''}
                          onChange={(e) => setEditingItem({...editingItem, videoUrl: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com/video.mp4"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ảnh content
                        </label>
                        
                        {/* Tabs cho URL và Upload */}
                        <div className="mb-2">
                          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                            <button
                              type="button"
                              onClick={() => setContentImageInputType('url')}
                              className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                                contentImageInputType === 'url' 
                                  ? 'bg-white text-blue-600 shadow-sm' 
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              URL
                            </button>
                            <button
                              type="button"
                              onClick={() => setContentImageInputType('upload')}
                              className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                                contentImageInputType === 'upload' 
                                  ? 'bg-white text-blue-600 shadow-sm' 
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              Upload
                            </button>
                          </div>
                        </div>
                        
                        {/* URL Input */}
                        {contentImageInputType === 'url' && (
                          <div>
                            <input
                              type="url"
                              value={editingItem.contentImageUrl || ''}
                              onChange={(e) => setEditingItem({...editingItem, contentImageUrl: e.target.value, contentImageFile: undefined})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="https://example.com/content-image.jpg"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Nhập URL ảnh từ internet
                            </p>
                          </div>
                        )}
                        
                        {/* Upload Input */}
                        {contentImageInputType === 'upload' && (
                          <div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleContentImageChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Chọn ảnh từ máy tính (JPG, PNG, WEBP, tối đa 10MB)
                            </p>
                          </div>
                        )}
                        
                        {/* Preview */}
                        {(editingItem.contentImageUrl || editingItem.contentImageFile) && (
                          <div className="mt-3">
                            <div className="text-xs text-gray-500 mb-2">
                              Preview ảnh content: {editingItem.contentImageFile ? 'File đã chọn (chưa upload)' : editingItem.contentImageUrl}
                            </div>
                            <img
                              src={editingItem.contentImageUrl}
                              alt="Preview"
                              className="w-full h-32 object-cover rounded border"
                              onError={(e) => {
                                console.error('❌ Content image load error:', editingItem.contentImageUrl);
                                e.currentTarget.style.display = 'none';
                              }}
                              onLoad={() => {
                                console.log('✅ Content image loaded successfully:', editingItem.contentImageUrl);
                              }}
                            />
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500 mt-1">
                          Ảnh này sẽ hiển thị trong nội dung bài viết
                        </p>
                      </div>
                    </>
                  )}

                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-white rounded-md transition-colors ${
                      ('videoUrl' in editingItem) 
                        ? 'bg-blue-500 hover:bg-blue-600' 
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    Lưu
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 overflow-hidden shadow-xl">
            {/* Modal Header */}
            <div className="bg-red-50 px-6 py-4 border-b border-red-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-red-800">
                    Xác nhận xóa {('videoUrl' in deletingItem) ? 'video' : 'công thức'}
                  </h3>
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Bạn có chắc chắn muốn xóa {('videoUrl' in deletingItem) ? 'video' : 'công thức'} này không?
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="font-medium text-gray-900">{deletingItem.title}</div>
                <div className="text-sm text-gray-500 mt-1">{deletingItem.description}</div>
                <div className="text-xs text-gray-400 mt-2">
                  ID: {deletingItem.id || deletingItem._id}
                </div>
              </div>
              
              <p className="text-xs text-red-600">
                ⚠️ Hành động này không thể hoàn tác!
              </p>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCloseDeleteModal}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Xóa {('videoUrl' in deletingItem) ? 'video' : 'công thức'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Icons */}
      <SocialMediaIcons />
    </div>
  );
};


// Social Links Management Component
interface SocialLinksManagementProps {
  socialLinks: SocialLinksData | null;
  onSave: (facebook: string, telegram: string) => void;
}

const SocialLinksManagement: React.FC<SocialLinksManagementProps> = ({ socialLinks, onSave }) => {
  const [facebook, setFacebook] = useState(socialLinks?.facebook || '');
  const [telegram, setTelegram] = useState(socialLinks?.telegram || '');

  useEffect(() => {
    setFacebook(socialLinks?.facebook || '');
    setTelegram(socialLinks?.telegram || '');
  }, [socialLinks]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(facebook, telegram);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Quản lý Social Links</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
            Link Facebook
          </label>
          <input
            type="url"
            id="facebook"
            value={facebook}
            onChange={(e) => setFacebook(e.target.value)}
            placeholder="https://facebook.com/your-page"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="telegram" className="block text-sm font-medium text-gray-700 mb-2">
            Link Telegram
          </label>
          <input
            type="url"
            id="telegram"
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            placeholder="https://t.me/your-channel"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            {socialLinks ? 'Cập nhật' : 'Tạo mới'}
          </button>
          <button
            type="button"
            onClick={() => {
              setFacebook(socialLinks?.facebook || '');
              setTelegram(socialLinks?.telegram || '');
            }}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Reset
          </button>
        </div>
      </form>

      {/* Preview */}
      {(facebook || telegram) && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-medium mb-3">Preview Social Links:</h3>
          <div className="space-y-2">
            {facebook && (
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-medium">Facebook:</span>
                <a href={facebook} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                  {facebook}
                </a>
              </div>
            )}
            {telegram && (
              <div className="flex items-center gap-2">
                <span className="text-blue-600 font-medium">Telegram:</span>
                <a href={telegram} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                  {telegram}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageContent;
